import { createContext, useContext, useState, ReactNode } from 'react'
import CustomNotification, { NotificationData } from '../components/CustomNotification'

interface NotificationContextType {
  showSuccess: (title: string, message: string, duration?: number) => void
  showError: (title: string, message: string, duration?: number) => void
  showWarning: (title: string, message: string, duration?: number) => void
  showInfo: (title: string, message: string, duration?: number) => void
  showConfirm: (
    title: string, 
    message: string, 
    onConfirm: () => void, 
    onCancel?: () => void,
    confirmText?: string,
    cancelText?: string
  ) => void
  dismissNotification: (id: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([])

  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  const addNotification = (notification: Omit<NotificationData, 'id'>) => {
    const id = generateId()
    const newNotification: NotificationData = { ...notification, id }
    setNotifications(prev => [...prev, newNotification])
    return id
  }

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  const showSuccess = (title: string, message: string, duration: number = 5000) => {
    addNotification({
      type: 'success',
      title,
      message,
      duration
    })
  }

  const showError = (title: string, message: string, duration: number = 7000) => {
    addNotification({
      type: 'error',
      title,
      message,
      duration
    })
  }

  const showWarning = (title: string, message: string, duration: number = 6000) => {
    addNotification({
      type: 'warning',
      title,
      message,
      duration
    })
  }

  const showInfo = (title: string, message: string, duration: number = 5000) => {
    addNotification({
      type: 'info',
      title,
      message,
      duration
    })
  }

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
    confirmText?: string,
    cancelText?: string
  ) => {
    addNotification({
      type: 'confirm',
      title,
      message,
      duration: 0, // No auto-dismiss for confirmations
      onConfirm,
      onCancel,
      confirmText,
      cancelText
    })
  }

  const dismissNotification = (id: string) => {
    removeNotification(id)
  }

  const handleConfirm = (id: string) => {
    const notification = notifications.find(n => n.id === id)
    if (notification?.onConfirm) {
      notification.onConfirm()
    }
    removeNotification(id)
  }

  const handleCancel = (id: string) => {
    const notification = notifications.find(n => n.id === id)
    if (notification?.onCancel) {
      notification.onCancel()
    }
    removeNotification(id)
  }

  const value: NotificationContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    dismissNotification
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <CustomNotification
        notifications={notifications}
        onDismiss={dismissNotification}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
} 